import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useLocalProfile } from "../local/useLocalProfile";
import { useMpRoom } from "../multiplayer/useMpRoom";
import { getBattlePlayerId } from "../multiplayer/battleIdentity";
import { GameView } from "../game/GameView";
import {
  computeBonuses,
  rewardSettingsForLesson,
  type GameConfig,
  type RunOutcome,
} from "../game/types";
import { SEED_LESSONS, getSeedLesson } from "../data/seed-lessons";
import { recordRun } from "../local/profile";
import { loadGameSettings } from "../local/gameSettings";
import { sfx } from "../game/audio";
import { SINGLE_COURSE_LENGTH } from "../game/courseConfig";

function speedForLesson(orderIndex: number): number {
  return Math.min(22, 13 + Math.max(0, orderIndex - 1) * 0.7);
}

function playerLevel(coins: number): number {
  return 1 + Math.floor(coins / 200);
}

export function RacePlay() {
  const nav = useNavigate();
  const [params] = useSearchParams();
  const { profile, setProfile } = useLocalProfile();
  const code = params.get("code");

  if (!profile) {
    nav("/", { replace: true });
    return null;
  }
  const me = {
    id: getBattlePlayerId(profile.id, code),
    displayName: profile.displayName,
    avatarConfig: profile.avatarConfig,
  };

  const joinOpts = useMemo(() => (code ? { code, asHost: false, me } : null), [code, me.id]);
  const { client, state, positions } = useMpRoom(joinOpts);

  const lesson = state ? getSeedLesson(state.lessonId) ?? null : null;
  const finishReportedRef = useRef(false);
  const recordedRef = useRef(false);
  const playersKey = useMemo(
    () =>
      state?.players
        .map((p) => `${p.id}|${p.slotIdx}|${p.displayName}|${JSON.stringify(p.avatarConfig)}`)
        .join("~") ?? "",
    [state?.players],
  );

  const config = useMemo<GameConfig | null>(() => {
    if (!lesson || !state) return null;
    const myPlayer = state.players.find((p) => p.id === me.id);
    const rewards = rewardSettingsForLesson(lesson.orderIndex, lesson.coinsCount);
    const settings = loadGameSettings();
    return {
      lessonId: lesson.id,
      words: lesson.words,
      theme: lesson.theme,
      timeOfDay: lesson.timeOfDay,
      questionsCount: lesson.questionsCount,
      objectsCount: lesson.objectsCount,
      coinsCount: rewards.coinsCount,
      coinPickupValue: rewards.coinPickupValue,
      quizCorrectCoins: rewards.quizCorrectCoins,
      courseLength: SINGLE_COURSE_LENGTH,
      seed: state.seed,
      avatar: myPlayer?.avatarConfig ?? me.avatarConfig,
      challengeKinds: lesson.challengeKinds ?? null,
      upgrades: profile.upgrades,
      runSpeed: speedForLesson(lesson.orderIndex),
      movementMode: settings.movementMode,
      movementSensitivity: settings.movementSensitivity,
      cameraSensitivity: settings.cameraSensitivity,
      controllerDeadzone: settings.controllerDeadzone,
      remotePlayers: state.players
        .filter((p) => p.id !== me.id)
        .map((p) => ({
          id: p.id,
          displayName: p.displayName,
          avatarConfig: p.avatarConfig,
          slotIdx: p.slotIdx,
        })),
      mySlotIdx: myPlayer?.slotIdx ?? 0,
      spectateAfterDeath: true,
    };
  }, [
    lesson?.id,
    lesson,
    state?.seed,
    playersKey,
    me.id,
    profile.upgrades,
  ]);

  // Throttle our own position broadcast to ~12 Hz.
  const lastSentRef = useRef(0);
  const onMyTick = (s: number, laneOffset: number, runState: string) => {
    const now = performance.now();
    if (now - lastSentRef.current < 80) return;
    lastSentRef.current = now;
    client?.sendTick({
      s,
      laneOffset,
      state: runState as "run" | "jump" | "slide" | "hit" | "finished",
    });
  };

  const handleEnd = (o: RunOutcome) => {
    if (finishReportedRef.current) return;
    finishReportedRef.current = true;
    const bonus = computeBonuses(o);
    const correct = o.wordOutcomes.filter((w) => w.correct).length;
    client?.reportFinish({
      timeMs: o.timeMs,
      score: bonus.totalScore,
      correctCount: correct,
      totalQuestions: o.wordOutcomes.length,
    });
    // Persist coins / mastery / progress to the local profile.
    if (lesson && profile && !recordedRef.current) {
      recordedRef.current = true;
      const next = recordRun(profile, {
        lessonId: lesson.id,
        score: bonus.totalScore,
        timeMs: o.timeMs,
        coinsEarned: bonus.totalCoins,
        hearts: o.hearts,
        wordOutcomes: o.wordOutcomes,
        finished: o.finished,
      });
      if (playerLevel(next.coins) > playerLevel(profile.coins)) sfx.levelUp();
      setProfile(next);
    }
  };

  useEffect(() => {
    if (state?.phase === "results") {
      nav(`/battle/results?code=${state.code}`, { replace: true });
    }
  }, [state?.phase, nav, state?.code]);

  if (!code) {
    nav("/battle", { replace: true });
    return null;
  }
  if (!state || !config) return <div className="full-center">Loading race…</div>;
  if (state.phase === "lobby") {
    nav(`/battle?code=${state.code}`, { replace: true });
    return null;
  }

  const countdownLabel =
    (state.phase === "countdown" || state.phase === "racing") && state.countdownStartedAt
      ? (() => {
          const elapsed = Date.now() - state.countdownStartedAt;
          if (elapsed < 1000) return "3";
          if (elapsed < 2000) return "2";
          if (elapsed < 3000) return "1";
          if (elapsed < 3900) return "GO!";
          return null;
        })()
      : null;

  return (
    <GameView
      config={config}
      onEnd={handleEnd}
      positions={positions}
      onMyTick={onMyTick}
      paused={state.phase === "countdown"}
      countdownLabel={countdownLabel}
    />
  );
}

// Keep SEED_LESSONS imported so vite tree-shake doesn't drop it (used elsewhere).
void SEED_LESSONS;
